import { useState } from "react";
import { useLang } from "@/lib/i18n";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  MoonStar, Globe, ArrowRight, Users, CalendarDays, CreditCard, Plane,
  Network, Sparkles, Check, Menu, X, ClipboardList, BedDouble, FileCheck,
  Quote, Mail, Phone, MapPin
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const HERO_IMG = "https://images.unsplash.com/photo-1693590614566-1d3ea9ef32f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxtYWtrYWglMjBrYWFiYSUyMHBpbGdyaW1zJTIwdW1yYWglMjBoYWpqfGVufDB8fHx8MTc4OTI5NjU4M3ww&ixlib=rb-4.1.0&q=85";
const MADINAH_IMG = "https://images.unsplash.com/photo-1692566123227-0f68f1b9dac6?crop=entropy&cs=srgb&fm=jpg&q=85";
const PILGRIM_IMG = "https://images.unsplash.com/photo-1691566264354-88491ba17f15?crop=entropy&cs=srgb&fm=jpg&q=85";

const FLOW = ["LEAD", "CRM", "SALES", "PACKAGE", "BOOKING", "PAYMENT", "JEMAAH", "DEPARTURE", "OPERATION", "RETURN", "REPEAT"];

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

export default function Landing() {
  const { lang, toggle, t } = useLang();
  const navigate = useNavigate();
  const [annual, setAnnual] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const tiers = [
    { name: "STARTER", price: 299, desc: lang === "bm" ? "Untuk agensi kecil" : "For small agencies",
      feats: ["1 Branch / Cawangan", "5 Staff Users", "500 Customer Records", "Package & Departure", "Basic CRM + Booking", "Payments & Reports"] },
    { name: "PROFESSIONAL", price: 799, popular: true, desc: lang === "bm" ? "Untuk agensi berkembang" : "For growing agencies",
      feats: ["3 Branches / Cawangan", "15 Staff Users", "Agent Management + Commission", "Rooming, Visa, Flight", "Supplier & Operations", "Advanced Reports"] },
    { name: "BUSINESS", price: 1499, desc: lang === "bm" ? "Untuk agensi mantap" : "For established agencies",
      feats: ["10 Branches / Cawangan", "50 Staff Users", "AI Travel Assistant", "Omnichannel CRM", "Sales Target & Profitability", "API Access"] },
    { name: "ENTERPRISE", price: 2999, desc: lang === "bm" ? "Untuk kumpulan besar" : "For large groups",
      feats: ["Multi-Company", "Custom Branch Limits", "Large Agent Network", "SSO & Custom Workflow", "Dedicated Environment", "Priority Support + SLA"] },
  ];

  const features = [
    { icon: Users, k: "crm", big: true },
    { icon: CalendarDays, k: "pkg" },
    { icon: CreditCard, k: "book" },
    { icon: Network, k: "agent" },
    { icon: Sparkles, k: "ai", big: true },
    { icon: Plane, k: "ops" },
  ];

  return (
    <div className="min-h-screen bg-sand text-slate-900">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 bg-white/85 backdrop-blur-md">
        <div className="bg-emerald-800 text-emerald-50 text-center text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.15em] py-1.5 px-4" data-testid="promo-banner">
          MATTA Fair Special — Pay 10 months, get 12 on annual plans
        </div>
        <div className="border-b border-sand-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <img src="/logo.jpeg" alt="Travel Hub Desk" className="w-9 h-9 rounded-lg object-cover" />
            <div className="leading-none">
              <span className="font-serif text-lg tracking-tight text-slate-900">Travel Hub Desk</span>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-500">
            <a href="#features" data-testid="nav-features-link" className="hover:text-emerald-800 transition-colors">{t("nav.features")}</a>
            <a href="#flow" data-testid="nav-flow-link" className="hover:text-emerald-800 transition-colors">{t("nav.pipeline")}</a>
            <a href="#pricing" data-testid="nav-pricing-link" className="hover:text-emerald-800 transition-colors">{t("nav.pricing")}</a>
            <Link to="/store" data-testid="nav-store-link" className="hover:text-emerald-800 transition-colors text-emerald-700 font-medium">{t("nav.store")}</Link>
          </nav>
          <div className="flex items-center gap-3">
            <button data-testid="language-toggle-button" onClick={toggle}
              className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-emerald-800 transition-colors">
              <Globe className="w-4 h-4" />{lang === "en" ? "BM" : "EN"}
            </button>
            <Button data-testid="nav-login-button" variant="ghost" onClick={() => navigate("/login")}
              className="hidden sm:inline-flex text-slate-600 hover:text-slate-900 hover:bg-slate-100">{t("nav.login")}</Button>
            <Button data-testid="nav-trial-button" onClick={() => navigate("/login")}
              className="bg-emerald-800 hover:bg-emerald-700 text-white font-medium rounded-full px-5 shadow-sm">{t("nav.trial")}</Button>
            <button className="md:hidden text-slate-700" data-testid="mobile-menu-button" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t border-sand-border bg-white px-6 py-4 space-y-3" data-testid="mobile-menu">
            <a href="#features" className="block text-slate-600" onClick={() => setMobileOpen(false)}>{t("nav.features")}</a>
            <a href="#pricing" className="block text-slate-600" onClick={() => setMobileOpen(false)}>{t("nav.pricing")}</a>
            <Link to="/login" className="block text-emerald-700 font-medium">{t("nav.login")}</Link>
          </div>
        )}
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-40 pb-20 overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-12 items-center">
            <div>
              <motion.p variants={fadeUp} initial="hidden" animate="show"
                className="text-xs font-mono uppercase tracking-[0.3em] text-emerald-700 mb-6">{t("hero.eyebrow")}</motion.p>
              <motion.h1 variants={fadeUp} initial="hidden" animate="show" transition={{ delay: 0.1 }}
                className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-slate-900">
                {t("hero.title1")}{" "}
                <span className="text-emerald-800 italic">{t("hero.title2")}</span>
              </motion.h1>
              <motion.p variants={fadeUp} initial="hidden" animate="show" transition={{ delay: 0.2 }}
                className="mt-6 text-slate-500 text-base sm:text-lg max-w-xl leading-relaxed">{t("hero.sub")}</motion.p>
              <motion.div variants={fadeUp} initial="hidden" animate="show" transition={{ delay: 0.3 }} className="mt-10 flex flex-wrap gap-4">
                <Button data-testid="hero-demo-button" onClick={() => navigate("/login")} size="lg"
                  className="bg-emerald-800 hover:bg-emerald-700 text-white font-semibold rounded-full px-8 h-13 text-base shadow-sm">
                  {t("hero.cta1")} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <Button data-testid="hero-trial-button" onClick={() => navigate("/login")} size="lg" variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-white rounded-full px-8 h-13 text-base bg-white">
                  {t("hero.cta2")}
                </Button>
              </motion.div>
              <motion.div variants={fadeUp} initial="hidden" animate="show" transition={{ delay: 0.38 }}
                className="mt-8 flex flex-wrap gap-2" data-testid="hero-module-pills">
                {["CRM", "Package", "Booking", "Inventory", "Agents", "Operations", "Payments"].map((m) => (
                  <span key={m} className="px-3.5 py-1.5 rounded-full bg-white border border-sand-border text-[11px] font-mono uppercase tracking-wider text-slate-600 shadow-sm">{m}</span>
                ))}
              </motion.div>
              <motion.div variants={fadeUp} initial="hidden" animate="show" transition={{ delay: 0.4 }}
                className="mt-8 flex items-center gap-4 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400">
                <span>Trusted by 85+ agencies</span>
                <span className="w-8 h-px bg-slate-300" />
                <span>MATTA Fair Ready</span>
              </motion.div>
            </div>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
              className="hidden lg:block relative" data-testid="hero-preview-card">
              <img src={HERO_IMG} alt="Makkah" className="absolute -top-12 -right-4 w-56 h-40 object-cover rounded-2xl shadow-xl rotate-3 border-4 border-white z-0" />
              <div className="relative z-10 mt-14 rounded-2xl border border-sand-border bg-white shadow-[0_24px_64px_-24px_rgba(15,23,42,0.18)] overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b border-sand-border">
                  <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Live Operations — Today</span>
                  <span className="flex items-center gap-1.5 text-[10px] font-mono text-emerald-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />LIVE
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-px bg-sand-border/60">
                  {[["New Leads", "63"], ["Bookings", "18"], ["Sales", "RM112,800"], ["Seats Left", "342"]].map(([l, v]) => (
                    <div key={l} className="bg-white px-5 py-4">
                      <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-slate-400">{l}</div>
                      <div className="font-sans text-xl font-semibold text-slate-900 mt-1 tabular-nums">{v}</div>
                    </div>
                  ))}
                </div>
                <div className="p-5">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400 mb-3">Departure Readiness</div>
                  {[["Umrah Signature 5-Star — 8 Nov", 94], ["Umrah Barakah — 22 Nov", 81], ["Hajj Tamattu — 15 Dec", 67]].map(([l, v]) => (
                    <div key={l} className="mb-3 last:mb-0">
                      <div className="flex justify-between text-[11px] mb-1.5">
                        <span className="text-slate-600">{l}</span>
                        <span className="font-mono text-emerald-700 tabular-nums">{v}%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-gold-500" style={{ width: `${v}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
          <motion.div variants={fadeUp} initial="hidden" animate="show" transition={{ delay: 0.45 }}
            className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-px bg-sand-border/70 rounded-2xl overflow-hidden border border-sand-border">
            {[["85+", t("hero.stat1")], ["32,500+", t("hero.stat2")], ["RM96M+", t("hero.stat3")], ["CRM→AI", t("hero.stat4")]].map(([v, l], i) => (
              <div key={i} className="bg-white p-6" data-testid={`hero-stat-${i}`}>
                <div className="font-serif text-2xl sm:text-3xl text-emerald-800 tabular-nums">{v}</div>
                <div className="text-xs text-slate-500 mt-1 font-mono uppercase tracking-wider">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Image gallery */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-3 gap-4">
          {[
            { src: HERO_IMG, en: "Makkah — Kaaba", bm: "Makkah — Kaabah" },
            { src: MADINAH_IMG, en: "Madinah — Masjid Nabawi", bm: "Madinah — Masjid Nabawi" },
            { src: PILGRIM_IMG, en: "Guided Jemaah Operations", bm: "Operasi Jemaah Berpandu" },
          ].map((img, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="relative rounded-2xl overflow-hidden h-56 sm:h-64 group border border-sand-border" data-testid={`gallery-img-${i}`}>
              <img src={img.src} alt={img.en} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-brand-950/80 to-transparent p-4">
                <span className="text-white text-xs font-mono uppercase tracking-widest">{lang === "bm" ? img.bm : img.en}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Flow strip */}
      <section id="flow" className="py-20 border-y border-sand-border bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-emerald-700 mb-3">{t("flow.eyebrow")}</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight max-w-2xl">{t("flow.title")}</h2>
          <p className="text-slate-500 mt-4 max-w-3xl text-sm sm:text-base">{t("flow.sub")}</p>
          <div className="mt-10 flex flex-wrap items-center gap-2" data-testid="flow-pipeline">
            {FLOW.map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <motion.span initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                  className={`px-4 py-2 rounded-full text-xs font-mono tracking-wider border ${i % 3 === 0 ? "border-emerald-700/40 text-emerald-800 bg-emerald-50" : "border-sand-border text-slate-600 bg-white"}`}>
                  {s}
                </motion.span>
                {i < FLOW.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-slate-300" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features bento */}
      <section id="features" className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-emerald-700 mb-3">{t("feat.eyebrow")}</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight max-w-3xl">{t("feat.title")}</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-4">
            {features.map(({ icon: Icon, k, big }, i) => (
              <motion.div key={k} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                data-testid={`feature-card-${k}`}
                className={`relative rounded-2xl border border-sand-border bg-white p-8 hover:border-emerald-700/30 shadow-[0_1px_2px_rgba(0,0,0,0.03)] transition-all overflow-hidden group ${big ? "md:col-span-2" : ""}`}>
                <div className="relative">
                  {big && (
                    <>
                      <img src={k === "ai" ? MADINAH_IMG : HERO_IMG} alt=""
                        className="absolute inset-y-0 right-0 w-2/5 h-full object-cover opacity-20 [mask-image:linear-gradient(to_left,black_40%,transparent)]" />
                      <div className="absolute inset-y-0 right-0 w-2/5 bg-gradient-to-l from-white/40 to-transparent" />
                    </>
                  )}
                  <div className="w-11 h-11 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-emerald-700" />
                  </div>
                  <h3 className="font-sans font-semibold text-xl mb-2">{t(`feat.${k}`)}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed max-w-md">{t(`feat.${k}.d`)}</p>
                  {k === "ai" && (
                    <div className="mt-5 flex flex-wrap gap-2">
                      {[t("ai.q1"), t("ai.q2")].map((q) => (
                        <span key={q} className="text-xs px-3 py-1.5 rounded-full bg-sand border border-sand-border text-slate-500 font-mono">{q}</span>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
          <div className="mt-4 grid md:grid-cols-3 gap-4">
            {[{ icon: BedDouble, en: "Rooming Management", bm: "Pengurusan Bilik", d: "Quad, Triple, Double, family rooms & roommate allocation", dbm: "Quad, Triple, Double, bilik keluarga & agihan rakan sebilik" },
              { icon: FileCheck, en: "Visa & Documents", bm: "Visa & Dokumen", d: "Track visa status, passport expiry & document checklists", dbm: "Jejak status visa, tamat passport & senarai semak dokumen" },
              { icon: ClipboardList, en: "Readiness Score", bm: "Skor Kesediaan", d: "Departure readiness % across payment, visa, ticket, rooming", dbm: "% kesediaan pelepasan merentasi bayaran, visa, tiket, rooming" }].map((f, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="rounded-2xl border border-sand-border bg-white p-6 hover:border-emerald-700/30 shadow-[0_1px_2px_rgba(0,0,0,0.03)] transition-all"
                data-testid={`feature-mini-${i}`}>
                <f.icon className="w-5 h-5 text-emerald-700 mb-3" />
                <h4 className="font-medium text-sm">{lang === "bm" ? f.bm : f.en}</h4>
                <p className="text-slate-500 text-xs mt-1.5 leading-relaxed">{lang === "bm" ? f.dbm : f.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 border-t border-sand-border">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-emerald-700 mb-3">{t("testi.eyebrow")}</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-slate-900">{t("testi.title")}</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                data-testid={`testimonial-${i}`}
                className="bg-white rounded-2xl border border-sand-border p-7 shadow-[0_1px_2px_rgba(0,0,0,0.03)] hover:border-emerald-700/30 transition-all">
                <Quote className="w-5 h-5 text-emerald-600 mb-4" />
                <p className="text-sm text-slate-600 leading-relaxed">&ldquo;{t(`testi.${i}.q`)}&rdquo;</p>
                <div className="mt-6 pt-4 border-t border-sand-border">
                  <div className="text-sm font-semibold text-slate-900">{t(`testi.${i}.n`)}</div>
                  <div className="text-[10px] text-slate-400 font-mono uppercase tracking-wider mt-0.5">{t(`testi.${i}.r`)}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 border-t border-sand-border bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.3em] text-emerald-700 mb-3">{t("price.eyebrow")}</p>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight">{t("price.title")}</h2>
            </div>
            <div className="flex items-center gap-1 p-1 rounded-full border border-sand-border bg-sand" data-testid="billing-toggle">
              {[t("price.monthly"), t("price.annual")].map((l, i) => (
                <button key={l} data-testid={`billing-${i === 0 ? "monthly" : "annual"}`}
                  onClick={() => setAnnual(i === 1)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${annual === (i === 1) ? "bg-emerald-800 text-white" : "text-slate-500 hover:text-slate-800"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {tiers.map((tier, i) => {
              const price = annual ? Math.round(tier.price * 10 / 12) : tier.price;
              return (
                <motion.div key={tier.name} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                  data-testid={`pricing-tier-${tier.name.toLowerCase()}`}
                  className={`relative rounded-2xl border p-7 flex flex-col transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_24px_48px_-16px_rgba(4,120,87,0.18)] ${tier.popular ? "border-emerald-700 bg-emerald-50/50 shadow-[0_16px_40px_-16px_rgba(4,120,87,0.25)]" : "border-sand-border bg-white hover:border-slate-300"}`}>>
                  {tier.popular && (
                    <span className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-emerald-800 text-white text-[10px] font-mono uppercase tracking-widest">{t("price.popular")}</span>
                  )}
                  <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">{tier.name}</h3>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className="font-serif text-4xl text-slate-900">RM{price.toLocaleString()}</span>
                    <span className="text-slate-400 text-xs">{t("price.permonth")}</span>
                  </div>
                  <p className="text-slate-500 text-xs mt-1.5">{tier.desc}</p>
                  <ul className="mt-6 space-y-2.5 flex-1">
                    {tier.feats.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-xs text-slate-600">
                        <Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />{f}
                      </li>
                    ))}
                  </ul>
                  <Button data-testid={`pricing-cta-${tier.name.toLowerCase()}`} onClick={() => navigate("/login")}
                    className={`mt-8 rounded-full font-semibold ${tier.popular ? "bg-emerald-800 hover:bg-emerald-700 text-white" : "bg-sand hover:bg-slate-100 text-slate-800 border border-sand-border"}`}>
                    {tier.name === "ENTERPRISE" ? t("price.contact") : t("price.start")}
                  </Button>
                </motion.div>
              );
            })}
          </div>
          <p className="text-slate-400 text-xs mt-8 text-center">{t("price.note")}</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 border-t border-sand-border">
        <div className="max-w-3xl mx-auto px-6">
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-emerald-700 mb-3">{t("faq.eyebrow")}</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-slate-900">{t("faq.title")}</h2>
          <Accordion type="single" collapsible className="mt-10 space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}
                className="bg-white border border-sand-border rounded-xl px-5">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-sm font-medium text-slate-900 hover:no-underline py-4 text-left">
                  {t(`faq.${i}.q`)}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-slate-500 pb-4 leading-relaxed">
                  {t(`faq.${i}.a`)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 relative overflow-hidden">
        <img src={MADINAH_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-brand-950/90" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">{t("cta.title")}</h2>
          <p className="text-emerald-100/60 mt-5">{t("cta.sub")}</p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Button data-testid="cta-trial-button" onClick={() => navigate("/login")} size="lg"
              className="bg-gold-500 hover:bg-gold-600 text-brand-950 font-semibold rounded-full px-8">{t("hero.cta2")}</Button>
            <Button data-testid="cta-demo-button" onClick={() => navigate("/login")} size="lg" variant="outline"
              className="border-white/25 text-white hover:bg-white/10 rounded-full px-8 bg-transparent">{t("hero.cta1")}</Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-sand-border bg-white">
        <div className="max-w-7xl mx-auto px-6 py-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-2.5">
              <img src="/logo.jpeg" alt="Travel Hub Desk" className="w-8 h-8 rounded-lg object-cover" />
              <div className="leading-none">
                <span className="font-serif text-lg text-slate-900">Travel Hub Desk</span>
              </div>
            </div>
            <p className="text-slate-500 text-xs mt-4 leading-relaxed max-w-xs">{t("footer.desc")}</p>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400 mb-4">Product</div>
            <div className="space-y-2.5 text-sm">
              <a href="#features" className="block text-slate-500 hover:text-emerald-800 transition-colors">{t("nav.features")}</a>
              <a href="#pricing" className="block text-slate-500 hover:text-emerald-800 transition-colors">{t("nav.pricing")}</a>
              <Link to="/login" className="block text-slate-500 hover:text-emerald-800 transition-colors">{t("nav.trial")}</Link>
              <Link to="/login" className="block text-slate-500 hover:text-emerald-800 transition-colors">{t("hero.cta1")}</Link>
            </div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400 mb-4">Company</div>
            <div className="space-y-2.5 text-sm">
              <span className="block text-slate-500">About</span>
              <span className="block text-slate-500">Partners</span>
              <span className="block text-slate-500">Careers</span>
              <span className="block text-slate-500">Support</span>
            </div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400 mb-4">Contact</div>
            <div className="space-y-2.5 text-xs text-slate-500">
              <span className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-emerald-700" />hello@travelos.my</span>
              <span className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-emerald-700" />+603-2181 8888</span>
              <span className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-emerald-700" />Kuala Lumpur, Malaysia</span>
            </div>
          </div>
        </div>
        <div className="border-t border-sand-border">
          <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-slate-400 text-xs">© 2026 Travel Hub Desk. {t("footer.rights")}</p>
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400">Made for Muslim travel agencies</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
